import { XMLValidator, XMLParser } from 'fast-xml-parser';
import shellapi from 'a365-shell-api';
import { IResponseOptionsArgs } from './interfaces';

/**
 * Response class is used here so we adhere to the old Angular Http Client specs
 * This is a way to have a consistent way for our app to use an Http client without
 * relying on an outside one, as long as we conert whatever client's response to
 * this response type.
 */
export class Response implements shellapi.http.IResponse {
    /**
     * response is ok if 200 >= status <= 299
     */
    public ok: boolean;
    public url: string;
    public status: number;
    public statusText: string | null;
    public headers: shellapi.http.IHeaders | null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _body: any;

    constructor(responseOptions: IResponseOptionsArgs) {
        this._body = responseOptions.body;
        this.status = responseOptions.status;
        this.ok = (this.status >= 200 && this.status <= 299);
        this.statusText = responseOptions.statusText;
        this.headers = responseOptions.headers;
        this.url = responseOptions.url;
    }

    public toString(): string {
        return '';
    }

    public hasBody(): boolean {
        return !!this._body;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public json(): any {

        if (typeof this._body === 'string' && this.headers.get('content-type') === 'application/xml') {
            if (XMLValidator.validate(this._body)) {
                return new XMLParser().parse(this._body);
            }
        }

        if (typeof this._body === 'string') {
            return JSON.parse(this._body as string);
        }

        if (this._body instanceof ArrayBuffer) {
            return JSON.parse(this.text());
        }

        return this._body;
    }

    public text(encodingHint: 'legacy' | 'iso-8859' = 'legacy'): string {
        try {
            if (this._body instanceof URLSearchParams) {
                return this._body.toString();
            }
            // In some versions of edge UrlSearchParams is not available
            // eslint-disable-next-line no-empty, @typescript-eslint/no-empty-function
        } catch (error) { }

        if (this._body instanceof ArrayBuffer) {
            switch (encodingHint) {
                case 'legacy':
                    const utf8Decoder = new TextDecoder(); // default 'utf-8' or 'utf8'
                    return utf8Decoder.decode(this._body);
                case 'iso-8859':
                    return String.fromCharCode.apply(null, new Uint8Array(this._body as ArrayBuffer));
                default:
                    throw new Error(`Invalid value for encodingHint: ${encodingHint}`);
            }
        }

        if (this._body == null) {
            return '';
        }

        if (this._body instanceof ProgressEvent) {
            // Do not stringify Event directly as we won't get anything useful.
            if (this._body.type === 'error') {
                // e.g. request transport failure (offline)
                return 'Browser HTTP transport failure.';
            }
            return `ProgressEvent: ${this._body.type}.`;
        }

        if (typeof this._body === 'object') {
            return JSON.stringify(this._body, null, 2);
        }

        return this._body.toString();
    }

    public arrayBuffer(): ArrayBuffer {
        if (this._body instanceof ArrayBuffer) {
            return this._body as ArrayBuffer;
        }

        return this._stringToArrayBuffer(this.text());
    }

    public blob(): Blob {
        if (this._body instanceof Blob) {
            return this._body as Blob;
        }

        if (this._body instanceof ArrayBuffer) {
            return new Blob([this._body]);
        }

        throw new Error('The request body isn\'t either a blob or an array buffer');
    }

    private _stringToArrayBuffer(input: string): ArrayBuffer {
        const view = new Uint16Array(input.length);

        for (let i = 0, strLen = input.length; i < strLen; i++) {
            view[i] = input.charCodeAt(i);
        }

        return view.buffer;
    }
}
